import {
  FindingAuditActionSchema,
  type FindingAuditLog,
} from '@terra/shared-contracts-findings';
import {
  formatPreventionStatusLabel,
  formatRetestOutcomeDisplay,
  parsePreventionAuditValue,
  PREVENTION_AUDIT_ACTIONS,
  readMetadataPrNumber,
  readMetadataString,
} from './finding-audit-format';
import {
  getLinkedTicketWorkItemKind,
  translateTicketingWorkItem,
} from '../linked-ticket-taxonomy';

const capitalizeTicketingWorkItemForDetail = (value: string): string =>
  value.length > 0
    ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
    : value;

type AuditTranslate = {
  (key: string): string;
  (key: string, values: Record<string, string | number>): string;
};

export const usesFindingStatusBadges = (log: FindingAuditLog): boolean =>
  log.action === FindingAuditActionSchema.enum.STATUS_CHANGED &&
  log.newValue !== 'Retest' &&
  Boolean(log.oldValue || log.newValue);

const ACTORLESS_LIFECYCLE_ACTIONS = new Set<FindingAuditLog['action']>([
  FindingAuditActionSchema.enum.ARCHIVED,
  FindingAuditActionSchema.enum.RESTORED,
]);

export const isActorlessLifecycleAction = (log: FindingAuditLog): boolean =>
  ACTORLESS_LIFECYCLE_ACTIONS.has(log.action);

const ACTIONS_WITH_REQUESTED_BY_IN_DETAIL = new Set<FindingAuditLog['action']>([
  FindingAuditActionSchema.enum.RETEST_SCHEDULED,
  FindingAuditActionSchema.enum.RETEST_STARTED,
  FindingAuditActionSchema.enum.REMEDIATION_PLAN_CREATED,
  FindingAuditActionSchema.enum.REMEDIATION_PR_CREATED,
]);

export const getFindingAuditTitle = ({
  log,
  actorDisplay,
  requestedByDisplay,
  providerDisplay,
  t,
}: {
  log: FindingAuditLog;
  actorDisplay: string;
  requestedByDisplay?: string;
  providerDisplay: string;
  t: AuditTranslate;
}): string => {
  const titleActor = requestedByDisplay ?? actorDisplay;

  if (log.action === FindingAuditActionSchema.enum.CREATED) {
    return t('audit.titles.CREATED');
  }

  if (log.action === FindingAuditActionSchema.enum.PUBLISHED) {
    return t('audit.titles.PUBLISHED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.UNPUBLISHED) {
    return t('audit.titles.UNPUBLISHED', { actor: actorDisplay });
  }

  if (
    log.action === FindingAuditActionSchema.enum.STATUS_CHANGED &&
    log.newValue === 'Retest'
  ) {
    return t('audit.titles.retestRequested', { actor: titleActor });
  }

  if (log.action === FindingAuditActionSchema.enum.STATUS_CHANGED) {
    return t('audit.titles.STATUS_CHANGED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.SEVERITY_CHANGED) {
    return t('audit.titles.SEVERITY_CHANGED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.SEVERITY_CHANGE_REQUESTED) {
    return t('audit.titles.SEVERITY_CHANGE_REQUESTED', {
      actor: actorDisplay,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.SEVERITY_CHANGE_REJECTED) {
    return t('audit.titles.SEVERITY_CHANGE_REJECTED', {
      actor: actorDisplay,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.CVSS_SCORE_CHANGED) {
    return t('audit.titles.CVSS_SCORE_CHANGED', { actor: actorDisplay });
  }

  if (
    log.action === FindingAuditActionSchema.enum.CVSS_VECTOR_CHANGED &&
    log.fieldName === 'cvss.version'
  ) {
    return t('audit.titles.cvssVersionChanged', { actor: actorDisplay });
  }

  if (
    log.action === FindingAuditActionSchema.enum.CVSS_VECTOR_CHANGED &&
    log.fieldName === 'cvss.environmentalVector'
  ) {
    return t('audit.titles.cvssEnvironmentalChanged', {
      actor: actorDisplay,
      version: readMetadataString(log.metadata, 'cvssVersion') ?? '',
    });
  }

  if (log.action === FindingAuditActionSchema.enum.CVSS_VECTOR_CHANGED) {
    return t('audit.titles.CVSS_VECTOR_CHANGED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.OWASP_RISK_LEVEL_CHANGED) {
    return t('audit.titles.OWASP_RISK_LEVEL_CHANGED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.ASSIGNED) {
    return t('audit.titles.ASSIGNED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_ADDED) {
    const workItem = translateTicketingWorkItem({
      t,
      kind: getLinkedTicketWorkItemKind({
        provider: readMetadataString(log.metadata, 'provider'),
      }),
    });

    return t('audit.titles.EXTERNAL_REFERENCE_ADDED', {
      actor: actorDisplay,
      provider: providerDisplay,
      workItem,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_REMOVED) {
    const workItem = translateTicketingWorkItem({
      t,
      kind: getLinkedTicketWorkItemKind({
        provider: readMetadataString(log.metadata, 'provider'),
      }),
    });

    return t('audit.titles.EXTERNAL_REFERENCE_REMOVED', {
      actor: actorDisplay,
      provider: providerDisplay,
      workItem,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_UPDATED) {
    const workItem = translateTicketingWorkItem({
      t,
      kind: getLinkedTicketWorkItemKind({
        provider: readMetadataString(log.metadata, 'provider'),
      }),
    });

    return t('audit.titles.EXTERNAL_REFERENCE_UPDATED', {
      actor: actorDisplay,
      provider: providerDisplay,
      workItem,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_SCHEDULED) {
    return t('audit.titles.RETEST_SCHEDULED');
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_STARTED) {
    return t('audit.titles.RETEST_STARTED');
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED) {
    return t('audit.titles.RETEST_COMPLETED');
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_ABORTED) {
    return t('audit.titles.RETEST_ABORTED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PLAN_CREATED) {
    return t('audit.titles.REMEDIATION_PLAN_CREATED');
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_CREATED) {
    return t('audit.titles.REMEDIATION_PR_CREATED');
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_MERGED) {
    return t('audit.titles.REMEDIATION_PR_MERGED');
  }

  if (log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_STAGED) {
    return t('audit.titles.PREVENTION_CONTROL_STAGED');
  }

  if (
    log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_ACTIVATED
  ) {
    return t('audit.titles.PREVENTION_CONTROL_ACTIVATED', {
      actor: actorDisplay,
    });
  }

  if (
    log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_REJECTED
  ) {
    return t('audit.titles.PREVENTION_CONTROL_REJECTED', {
      actor: actorDisplay,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_REMOVED) {
    return t('audit.titles.PREVENTION_CONTROL_REMOVED', {
      actor: actorDisplay,
    });
  }

  if (log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_DRIFTED) {
    return t('audit.titles.PREVENTION_CONTROL_DRIFTED');
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_LINKED) {
    return t('audit.titles.REMEDIATION_PR_LINKED', { actor: actorDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.ARCHIVED) {
    return t('audit.titles.ARCHIVED');
  }

  if (log.action === FindingAuditActionSchema.enum.RESTORED) {
    return t('audit.titles.RESTORED');
  }

  return t(`audit.actions.${log.action}`);
};

export const shouldAppendRequestedByLine = ({
  log,
  requestedByDisplay,
  executorDisplay,
}: {
  log: FindingAuditLog;
  requestedByDisplay?: string;
  executorDisplay: string;
}): boolean => {
  if (!requestedByDisplay) {
    return false;
  }

  if (requestedByDisplay === executorDisplay) {
    return false;
  }

  if (
    log.action === FindingAuditActionSchema.enum.STATUS_CHANGED &&
    log.newValue === 'Retest'
  ) {
    return false;
  }

  return !ACTIONS_WITH_REQUESTED_BY_IN_DETAIL.has(log.action);
};

export const getFindingAuditDetailText = ({
  log,
  requestedByDisplay,
  t,
  formatTimestamp,
}: {
  log: FindingAuditLog;
  requestedByDisplay?: string;
  t: AuditTranslate;
  formatTimestamp?: (value: string) => string;
}): string | undefined => {
  if (log.action === FindingAuditActionSchema.enum.PUBLISHED) {
    return undefined;
  }

  if (log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_UPDATED) {
    const oldUrl =
      readMetadataString(log.metadata, 'previousExternalUrl') ?? log.oldValue;
    const newUrl =
      readMetadataString(log.metadata, 'externalUrl') ?? log.newValue;

    if (oldUrl && newUrl) {
      return t('audit.details.urlChanged', { oldUrl, newUrl });
    }

    return undefined;
  }

  if (
    log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_ADDED ||
    log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_REMOVED
  ) {
    const ticketKey = readMetadataString(log.metadata, 'externalKey');

    if (!ticketKey) {
      return undefined;
    }

    const workItem = capitalizeTicketingWorkItemForDetail(
      translateTicketingWorkItem({
        t,
        kind: getLinkedTicketWorkItemKind({
          provider: readMetadataString(log.metadata, 'provider'),
        }),
      }),
    );

    return t('audit.details.workItemKey', { workItem, ticketKey });
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_SCHEDULED) {
    const rawRunAt =
      readMetadataString(log.metadata, 'runAt') ??
      readMetadataString(log.metadata, 'scheduledFor');
    const runAt = rawRunAt
      ? (formatTimestamp?.(rawRunAt) ?? rawRunAt)
      : undefined;

    if (requestedByDisplay && runAt) {
      return t('audit.details.retestScheduled', {
        actor: requestedByDisplay,
        runAt,
      });
    }

    if (requestedByDisplay) {
      return t('audit.details.requestedBy', { actor: requestedByDisplay });
    }

    if (runAt) {
      return t('audit.details.runAt', { runAt });
    }

    return undefined;
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_STARTED) {
    if (!requestedByDisplay) {
      return undefined;
    }

    return t('audit.details.requestedBy', { actor: requestedByDisplay });
  }

  if (log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED) {
    const rawOutcome =
      readMetadataString(log.metadata, 'outcome') ?? log.newValue ?? '';
    const summary = log.note?.trim() ?? '';

    if (rawOutcome === 'FAILED') {
      return summary.length > 0 ? summary : undefined;
    }

    const outcome = formatRetestOutcomeDisplay(rawOutcome);

    if (outcome && summary) {
      return t('audit.details.retestCompleted', { outcome, summary });
    }

    if (summary) {
      return summary;
    }

    if (outcome) {
      return outcome;
    }

    return undefined;
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PLAN_CREATED) {
    const repositoryName = readMetadataString(log.metadata, 'repositoryName');

    if (requestedByDisplay && repositoryName) {
      return t('audit.details.remediationPlan', {
        actor: requestedByDisplay,
        repositoryName,
      });
    }

    if (requestedByDisplay) {
      return t('audit.details.requestedBy', { actor: requestedByDisplay });
    }

    if (repositoryName) {
      return t('audit.details.repository', { repositoryName });
    }

    return undefined;
  }

  if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_CREATED) {
    const prNumber = readMetadataPrNumber(log.metadata);
    const prUrl = readMetadataString(log.metadata, 'prUrl');

    if (requestedByDisplay && prNumber && prUrl) {
      return t('audit.details.remediationPrCreated', {
        actor: requestedByDisplay,
        prNumber,
        prUrl,
      });
    }

    if (requestedByDisplay) {
      return t('audit.details.requestedBy', { actor: requestedByDisplay });
    }

    if (prNumber && prUrl) {
      return t('audit.details.pr', { prNumber, prUrl });
    }

    return undefined;
  }

  if (
    log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_MERGED ||
    log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_LINKED
  ) {
    const prNumber = readMetadataPrNumber(log.metadata);
    const prUrl = readMetadataString(log.metadata, 'prUrl');

    if (prNumber && prUrl) {
      return t('audit.details.pr', { prNumber, prUrl });
    }

    return undefined;
  }

  if (
    log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_REJECTED ||
    log.action === FindingAuditActionSchema.enum.SEVERITY_CHANGE_REJECTED
  ) {
    const reason = log.note?.trim();

    return reason && reason.length > 0 ? reason : undefined;
  }

  if (PREVENTION_AUDIT_ACTIONS.has(log.action)) {
    const oldLabel = parsePreventionAuditValue(log.oldValue);
    const newLabel = parsePreventionAuditValue(log.newValue);
    const fromValue = oldLabel
      ? formatPreventionStatusLabel(oldLabel.status)
      : null;
    const toValue = newLabel
      ? formatPreventionStatusLabel(newLabel.status)
      : null;

    if (fromValue && toValue) {
      return t('audit.details.fromTo', {
        oldValue: fromValue,
        newValue: toValue,
      });
    }

    if (toValue) {
      return t('audit.details.toOnly', { newValue: toValue });
    }

    return undefined;
  }

  if (log.action === FindingAuditActionSchema.enum.STATUS_CHANGED) {
    return undefined;
  }

  if (log.oldValue && log.newValue) {
    return t('audit.details.fromTo', {
      oldValue: log.oldValue,
      newValue: log.newValue,
    });
  }

  if (log.newValue) {
    return t('audit.details.toOnly', { newValue: log.newValue });
  }

  return undefined;
};
